import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  useSensors,
  useSensor,
  PointerSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Col, Layout, message, Row, Typography } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { get } from 'lodash';

import ResellerServices from 'services/reseller';

import { PageSpinner } from 'components/page-spinner';
import PdButton from 'components/pd-button';

import { errHandling } from 'utils';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

export function ResellerPage() {
  const navigate = useNavigate();
  const [benefitList, setBenefitList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageDetail, setPageDetail] = useState({});
  const [procedureList, setProcedureList] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  const {
    description = '',
    description_idn = '',
    desktop_media_url = '',
    title = '',
    title_idn = '',
  } = pageDetail;

  async function handleBenefitDragEnd(event) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = benefitList.findIndex(
        (item, index) => (item.id || index) === active.id
      );
      const newIndex = benefitList.findIndex(
        (item, index) => (item.id || index) === over.id
      );

      let list = [...benefitList];
      let temp = list[oldIndex];
      list[oldIndex] = list[newIndex];
      list[newIndex] = temp;

      let payload = [];
      list.forEach((item, index) => {
        let order_attr = {
          id: item.id,
          order_level: index,
        };
        payload.push(order_attr);
      });

      try {
        setBenefitList(list);
        await ResellerServices.createPageDetail({
          business_benefits_attributes: payload,
        });
        message.success(`Benefit order has been updated`);
      } catch (error) {
        errHandling(error);
      }
    }
  }

  async function handleProcedureDragEnd(event) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = procedureList.findIndex(
        (item, index) => (item.id || index) === active.id
      );
      const newIndex = procedureList.findIndex(
        (item, index) => (item.id || index) === over.id
      );

      let list = [...procedureList];
      let temp = list[oldIndex];
      list[oldIndex] = list[newIndex];
      list[newIndex] = temp;

      let payload = [];
      list.forEach((item, index) => {
        let order_attr = {
          id: item.id,
          order_level: index,
        };
        payload.push(order_attr);
      });

      try {
        setProcedureList(list);
        await ResellerServices.createPageDetail({
          business_procedures_attributes: payload,
        });
        message.success(`Procedure order has been updated`);
      } catch (error) {
        errHandling(error);
      }
    }
  }

  async function removeItem(id, page) {
    try {
      if (page === 'BENEFIT')
        await ResellerServices.createPageDetail({
          business_benefits_attributes: [{ id, _destroy: true }],
        });
      else if (page === 'STEPS')
        await ResellerServices.createPageDetail({
          business_procedures_attributes: [{ id, _destroy: true }],
        });
      message.success('Item has been removed');
      getPageDetail();
    } catch (error) {
      errHandling(error);
    }
  }

  async function getPageDetail() {
    setLoading(true);
    try {
      const { data } = await ResellerServices.getPageDetail();
      const detail = get(data, 'data', {});
      const { benefits = [], procedures = [] } = detail;
      setBenefitList(benefits);
      setPageDetail(detail);
      setProcedureList(procedures);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getPageDetail();
  }, []);

  if (loading) return <PageSpinner />;

  function SortableItem({ value, page, index }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: value.id || index,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      userSelect: 'none',
    };

    const {
      description = '',
      id = '',
      mobile_media_url = '',
      title = '',
      title_idn = '',
      description_idn = '',
    } = value;

    const handleEdit = () => {
      navigate('/resellers_page/edit', {
        state: { page, detail: value },
      });
    };

    return (
      <Row
        ref={setNodeRef}
        style={style}
        {...attributes}
        className="draggable-item mt-2 w-full"
        justify="space-between"
        type="flex"
        gutter={20}>
        <Col span={1}>
          <div {...listeners} style={{ cursor: 'grab', padding: '0 10px' }}>
            ⋮⋮
          </div>
        </Col>
        <Col span={3} {...listeners}>
          <img className="button-action" src={mobile_media_url} alt="banners" />
        </Col>
        <Col span={10} {...listeners}>
          {title && (
            <Title level={4} className="table-title--sort">
              {title}
            </Title>
          )}
          <Paragraph>{description}</Paragraph>
          <Title level={4} className="table-title--sort">
            {title_idn}
          </Title>
          <Paragraph>{description_idn}</Paragraph>
        </Col>
        <Col span={6}>
          <Row type="flex" justify="space-around">
            <Col span={11}>
              <PdButton
                full
                style={{ height: 32 }}
                type="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit();
                }}>
                Edit
              </PdButton>
            </Col>
            <Col span={11}>
              <PdButton
                full
                onClick={(e) => {
                  e.stopPropagation();
                  removeItem(id, page);
                }}
                style={{ height: 32 }}
                type="danger">
                Remove
              </PdButton>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }

  function SortableList({ items, page, onDragEnd }) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}>
        <SortableContext
          items={items.map((item, index) => ({ id: item.id || index }))}
          strategy={verticalListSortingStrategy}>
          {items.map((item, index) => (
            <SortableItem
              key={item.id}
              index={index}
              value={item}
              page={page}
            />
          ))}
        </SortableContext>
      </DndContext>
    );
  }

  return (
    <Layout className="main-container">
      <div className="content">
        <div className="pageTitle">Reseller Page</div>
        <Content className="pageContent">
          <Row gutter={18}>
            <Col span={7} offset={17} className="float-right">
              <Link
                to="/resellers_page/create"
                state={{ page: 'BANNER', detail: pageDetail }}>
                <PdButton
                  full
                  outline
                  style={{ height: 32, width: 176 }}
                  type="primary">
                  {`${title ? 'Edit' : 'Add New'} Banner`}
                </PdButton>
              </Link>
            </Col>
            <Col span={24}>
              <Col span={6}>
                <img
                  alt="banners"
                  className="button-action"
                  src={desktop_media_url}
                />
              </Col>
              <Col span={10} offset={1}>
                <Title level={4}>{title}</Title>
                <Paragraph>{description}</Paragraph>
                <Title level={4}>{title_idn}</Title>
                <Paragraph>{description_idn}</Paragraph>
              </Col>
            </Col>
            <Col span={24} className="hidden-title-container">
              <Row
                className="pageTitle hiddenTitle"
                justify="space-between"
                type="flex">
                <Col span={6}>
                  <div>First Content</div>
                </Col>
                <Col span={6} className="float-right">
                  <Link
                    to="/resellers_page/create"
                    state={{ page: 'BENEFIT', detail: pageDetail }}>
                    <PdButton
                      full
                      outline
                      style={{ height: 32, width: 176 }}
                      type="primary">
                      Add New Benefit
                    </PdButton>
                  </Link>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row type="flex" align="middle" id="drag-content-benefit">
                <SortableList
                  items={benefitList}
                  onDragEnd={handleBenefitDragEnd}
                  page="BENEFIT"
                />
              </Row>
            </Col>
            <Col span={24} className="hidden-title-container">
              <Row
                className="pageTitle hiddenTitle"
                justify="space-between"
                type="flex">
                <Col span={6}>
                  <div>Second Content</div>
                </Col>
                <Col span={6} className="float-right">
                  <Link
                    to="/resellers_page/create"
                    state={{ page: 'STEPS', detail: pageDetail }}>
                    <PdButton
                      full
                      outline
                      style={{ height: 32, width: 176 }}
                      type="primary">
                      Add New Procedure
                    </PdButton>
                  </Link>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row type="flex" align="middle" id="drag-content-procedure">
                <SortableList
                  items={procedureList}
                  onDragEnd={handleProcedureDragEnd}
                  page="STEPS"
                />
              </Row>
            </Col>
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { ResellerPage };
